import { useTracking } from '@repo/tracking/use-tracking';
import { Trans, useTranslation } from '@repo/translations/use-translation';
import { useState } from 'react';

import { useCartContext } from '@zooplus/cart-provider';
import { ButtonAnchor, PrimaryButton, SecondaryButton } from '@zooplus/ui-components';

import { useAddMembershipToCart } from '../../../../hooks/useCart';
import { useMembershipOffer } from '../../../../hooks/useMembershipOffer';
import { scrollToElement } from '../../../../hooks/useScrollTo';
import { useWindowSize } from '../../../../hooks/useWindowsSize';
import { PremiumAddedModal } from '../../../UI/PremiumAddedModal/PremiumAddedModal';
import { ProgramLogo } from '../../../UI/ProgramLogo/ProgramLogo';
import { PROGRAM_PLANS_ANCHOR_ID } from '../../program-plan/ProgramPlan/ProgramPlan';

import styles from './BannerContent.module.css';

interface BannerVariant {
  title: string;
  subtitle: string;
  subtitleValues: {
    endDate: string;
  };
  joinButton?: string;
  joinButtonNavigation?: string;
  variant: string;
}

interface BannerContentProps {
  bannerVariant: BannerVariant;
  isCustomerLoggedIn: boolean;
}

export const BannerContent = ({ bannerVariant, isCustomerLoggedIn }: BannerContentProps) => {
  const { t } = useTranslation();
  const { data: membership } = useMembershipOffer(isCustomerLoggedIn);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddingMembership, setIsAddingMembership] = useState(false);
  const { addMembershipToCart } = useAddMembershipToCart();
  const { cart, sid } = useCartContext();
  const isCartEmpty = cart?.articles?.length === 0;
  const { trackClick } = useTracking();
  const { innerWidth } = useWindowSize();

  const showSecondaryButton = membership !== undefined || bannerVariant.variant === 'not-logged';

  return (
    <div className={styles.bannerContent}>
      <div className={styles.text}>
        <ProgramLogo height={30} />
        <h1 data-testid="banner-title" className={styles.title}>
          {t(bannerVariant.title)}
        </h1>
        <p data-testid="banner-subtitle" className={styles.paragraph}>
          {bannerVariant.variant === 'premium' ? (
            <Trans
              i18nKey={bannerVariant.subtitle}
              values={{
                endDate: bannerVariant.subtitleValues.endDate,
              }}
            />
          ) : (
            t(bannerVariant.subtitle)
          )}
        </p>
      </div>
      {bannerVariant.variant !== 'premium' ? (
        <div className={styles.buttonsContainer}>
          {bannerVariant.variant === 'not-logged' ? (
            <ButtonAnchor
              onClick={() => {
                trackClick({
                  sid,
                  target: {
                    name: 'landing_page_banner',
                    id: 'sign_in_not_logged',
                  },
                });
              }}
              href={bannerVariant.joinButtonNavigation}
              data-testid="sign-in-button"
              length={innerWidth < 768 ? 'full' : undefined}
              className={styles.joinButton}>
              {bannerVariant.joinButton ? t(bannerVariant.joinButton) : null}
            </ButtonAnchor>
          ) : membership !== undefined ? (
            <PrimaryButton
              data-testid="join-button"
              length={innerWidth < 768 ? 'full' : undefined}
              className={styles.joinButton}
              isLoading={isAddingMembership}
              onClick={async () => {
                trackClick({
                  sid,
                  target: {
                    name: 'landing_page_banner',
                    id: 'join_logged',
                  },
                });

                setIsAddingMembership(true);
                try {
                  await addMembershipToCart(membership?.offer.articleId);
                  setIsModalOpen(true);
                } finally {
                  setIsAddingMembership(false);
                }
              }}>
              {bannerVariant.joinButton ? t(bannerVariant.joinButton) : null}
            </PrimaryButton>
          ) : null}
          {showSecondaryButton ? (
            <SecondaryButton
              data-testid="benefits-button"
              className={styles.benefitsButton}
              length={innerWidth < 768 ? 'full' : undefined}
              onClick={() => {
                scrollToElement(document.getElementById(PROGRAM_PLANS_ANCHOR_ID));
                trackClick({
                  sid,
                  target: {
                    name: 'landing_page_banner',
                    id: 'compare_plans_logged',
                  },
                });
              }}>
              {t('loyalty-program.public-landing-page.banner.benefits-button')}
            </SecondaryButton>
          ) : null}
        </div>
      ) : null}
      <PremiumAddedModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        isCartEmpty={isCartEmpty}
      />
    </div>
  );
};
